<template>
  <div class="shop-session stack tight">
    <UiPanel tone="econ">
      <div class="header">
        <div style="min-width: 0;">
          <h3 class="title">{{ ICONS.actions.trade }} Shop</h3>
          <div class="subtitle text-muted">
            <template v-if="npcName">{{ npcName }}</template>
            <template v-else>Select an NPC to trade.</template>
          </div>
        </div>
        <UiChip v-if="shopFreshness" :variant="shopFreshness.variant" class="stat-mono num">
          {{ shopFreshness.label }}
        </UiChip>
      </div>

      <div class="controls">
        <UiTabs v-model="mode" :tabs="modeTabs" tone="econ" />
        <label class="search">
          <span class="sr-only">Search shop</span>
          <input v-model="query" class="ui-input" placeholder="Search…" />
        </label>
      </div>
    </UiPanel>

    <UiCallout v-if="!npcId" variant="info">
      Choose an NPC in the world panel and press Trade.
    </UiCallout>
    <UiCallout v-else-if="error" variant="danger">{{ error }}</UiCallout>

    <template v-else-if="shop">
      <UiPanel tone="econ">
        <UiSectionHeader>
          <template #title>Prices</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(gold) }} gp</UiChip>
          </template>
        </UiSectionHeader>
        <div class="price-grid">
          <div class="kv">
            <div class="kv-label">Opinion</div>
            <div class="kv-value stat-mono num">{{ formatNumber(shop.opinion.effectiveOpinion) }}</div>
          </div>
          <div class="kv">
            <div class="kv-label">Bribe</div>
            <div class="kv-value stat-mono num">{{ formatNumber(shop.opinion.bribeCost) }} gp</div>
          </div>
        </div>
      </UiPanel>

      <UiPanel tone="econ" v-if="mode === 'buy'">
        <UiSectionHeader>
          <template #title>Stock</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(filteredStock.length) }}</UiChip>
          </template>
        </UiSectionHeader>

        <div v-if="filteredStock.length === 0" class="text-muted">No matching items.</div>
        <div v-else class="list">
          <template v-for="entry in filteredStock" :key="entry.id">
            <button
              type="button"
              class="row"
              :class="{ selected: entry.id === selectedStockId }"
              @click="selectStock(entry.id)"
            >
              <span class="icon" aria-hidden="true">{{ itemIconForType(entry.itemType) }}</span>
              <span class="row-main">
                <span class="name">{{ entry.itemName }}</span>
                <span class="text-muted stat-mono num">
                  {{ formatNumber(entry.price) }} gp · {{ formatNumber(entry.currentQty) }} qty · {{ markForItemId(entry.itemId) }}
                </span>
              </span>
            </button>

            <div v-if="entry.id === selectedStockId" class="trade-box trade-box--inline">
              <div class="trade-line">
                <UiChip variant="muted">
                  {{ itemIconForType(entry.itemType) }} {{ entry.itemName }} {{ markForItemId(entry.itemId) }}
                </UiChip>
                <UiChip variant="muted" class="stat-mono num">{{ formatNumber(entry.price) }} gp / unit</UiChip>
              </div>
              <div class="trade-controls">
                <div class="field">
                  <div class="field-label">Qty</div>
                  <UiQuantity v-model="qty" :min="1" :max="maxBuyQty" :disabled="loading || !selectedStock" />
                </div>
                <UiButton size="sm" :disabled="loading || !canBuy" @click="buySelected">
                  Buy <span class="stat-mono num">({{ formatNumber(buyTotal) }} gp)</span>
                </UiButton>
              </div>
              <div v-if="!canBuy" class="text-muted">Not enough gold or stock.</div>
            </div>
          </template>
        </div>
      </UiPanel>

      <UiPanel tone="econ" v-else>
        <UiSectionHeader>
          <template #title>Sell Inventory</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(filteredInventory.length) }}</UiChip>
          </template>
        </UiSectionHeader>

        <div v-if="filteredInventory.length === 0" class="text-muted">No matching items.</div>
        <div v-else class="list">
          <template v-for="entry in filteredInventory" :key="invKey(entry)">
            <button
              type="button"
              class="row"
              :class="{ selected: invKey(entry) === selectedInvKey }"
              @click="selectInventory(entry)"
            >
              <span class="icon" aria-hidden="true">{{ itemIconForType(entry.item?.type) }}</span>
              <span class="row-main">
                <span class="name">{{ entry.item?.name || entry.itemId }}</span>
                <span class="text-muted stat-mono num">×{{ formatNumber(entry.quantity) }} · {{ markForInventoryEntry(entry) }}</span>
              </span>
            </button>

            <div v-if="invKey(entry) === selectedInvKey" class="trade-box trade-box--inline">
              <div class="trade-line">
                <UiChip variant="muted">
                  {{ itemIconForType(entry.item?.type) }} {{ entry.item?.name || entry.itemId }} {{ markForInventoryEntry(entry) }}
                </UiChip>
                <UiChip variant="muted" class="stat-mono num">
                  {{ quote ? formatNumber(quote.sellUnit) : "—" }} gp / unit
                </UiChip>
              </div>
              <div class="trade-controls">
                <div class="field">
                  <div class="field-label">Qty</div>
                  <UiQuantity v-model="qty" :min="1" :max="entry.quantity" :disabled="loading || !quote" />
                </div>
                <UiButton size="sm" variant="secondary" :disabled="loading || !quote" @click="sellSelected">
                  Sell <span class="stat-mono num">(+{{ formatNumber(sellTotal) }} gp)</span>
                </UiButton>
              </div>
            </div>
          </template>
        </div>
      </UiPanel>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import type { InventoryEntry, ShopBrowseResponse, ShopQuoteResponse, ShopStockEntry } from "@idle-rpg/shared";
import { apiShopBuy, apiShopQuote, apiShopSell } from "../../../api/client";
import { useSessionSocket } from "../../../composables/useSessionSocket";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import { getItemMarkDisplay } from "../../../lib/itemMarks";
import { itemIconForType } from "../../../lib/itemIcons";
import { useItemMarkMode } from "../../../composables/useItemMarkMode";
import { useSessionStore } from "../../../store/session";
import { useToast } from "../../../composables/useToast";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiQuantity from "../../../components/ui/UiQuantity.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";
import UiTabs from "../../../components/ui/UiTabs.vue";

const props = defineProps<{
  npcId: string | null;
  npcName: string | null;
}>();

const session = useSessionStore();
const { addToast } = useToast();
const { mode: itemMarkMode } = useItemMarkMode();
const sessionSocket = useSessionSocket();
const lastUpdatedAt = ref<number | null>(null);
const SHOP_STALE_MS = 2 * 60 * 1000;
let shopWsUnsubscriber: (() => void) | null = null;

function markForItemId(itemId: string): string {
  return getItemMarkDisplay(itemMarkMode.value, itemId);
}

function markForInventoryEntry(entry: InventoryEntry): string {
  return getItemMarkDisplay(itemMarkMode.value, entry.itemId, entry.variantKey ?? null, entry.enchantment?.id ?? null);
}

const shop = ref<ShopBrowseResponse | null>(null);
const loading = ref(false);
const refreshing = ref(false);
const error = ref<string | null>(null);
const query = ref("");
const mode = ref<"buy" | "sell">("buy");
const qty = ref<number>(1);

const modeTabs = computed(() => [
  { value: "buy", label: "Buy", icon: ICONS.states.active },
  { value: "sell", label: "Sell", icon: ICONS.states.danger }
]);

const gold = computed(() => session.character?.gold ?? 0);

const filteredStock = computed(() => {
  const list = shop.value?.stock ?? [];
  const q = query.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter((s) => s.itemName.toLowerCase().includes(q));
});

const filteredInventory = computed(() => {
  const list = (session.character?.inventory ?? []).filter((e) => e.itemId !== "item-gold");
  const q = query.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter((e) => (e.item?.name || e.itemId).toLowerCase().includes(q));
});

const shopFreshness = computed(() => {
  if (!props.npcId) return { variant: "muted" as const, label: "Idle" };
  if (loading.value || refreshing.value) return { variant: "muted" as const, label: "Syncing" };
  if (lastUpdatedAt.value) {
    const stale = Date.now() - lastUpdatedAt.value > SHOP_STALE_MS;
    if (stale) return { variant: "warning" as const, label: "Stale" };
  }
  return { variant: "muted" as const, label: shop.value ? "Synced" : "—" };
});

function startShopStream() {
  if (!props.npcId) return;
  sessionSocket.subscribe(["shop"]);
  shopWsUnsubscriber?.();
  shopWsUnsubscriber = sessionSocket.onEvent("shop", (msg) => {
    if (msg.event !== "shop.updated") return;
    const data = msg.data;
    if (data && typeof data === "object" && "npcId" in data && (data as any).npcId !== props.npcId) return;
    void loadShop({ resetSelection: false, showLoading: false });
  });
}

function stopShopStream() {
  shopWsUnsubscriber?.();
  shopWsUnsubscriber = null;
  sessionSocket.unsubscribe(["shop"]);
}

const selectedStockId = ref<string | null>(null);
const selectedInvKey = ref<string | null>(null);
const selectedInv = ref<InventoryEntry | null>(null);
const quote = ref<ShopQuoteResponse | null>(null);

const selectedStock = computed<ShopStockEntry | null>(
  () => filteredStock.value.find((s) => s.id === selectedStockId.value) ?? null
);

const buyTotal = computed(() => {
  const entry = selectedStock.value;
  if (!entry) return 0;
  return Math.max(0, Math.floor(qty.value) * entry.price);
});

const maxBuyQty = computed(() => {
  const entry = selectedStock.value;
  if (!entry) return 1;
  const byStock = Math.max(0, entry.currentQty);
  const byGold = entry.price > 0 ? Math.floor(gold.value / entry.price) : 0;
  return Math.max(1, Math.min(byStock, byGold));
});

watch(
  () => maxBuyQty.value,
  (max) => {
    qty.value = Math.max(1, Math.min(Math.floor(qty.value), Math.max(1, Math.floor(max))));
  }
);

watch(
  () => selectedInv.value?.quantity ?? 1,
  (max) => {
    qty.value = Math.max(1, Math.min(Math.floor(qty.value), Math.max(1, Math.floor(max))));
  }
);

const canBuy = computed(() => {
  const entry = selectedStock.value;
  if (!entry) return false;
  const q = Math.max(1, Math.floor(qty.value));
  return q <= entry.currentQty && buyTotal.value <= gold.value;
});

const sellTotal = computed(() => {
  if (!quote.value) return 0;
  if (!selectedInv.value) return 0;
  return Math.max(0, Math.floor(qty.value) * quote.value.sellUnit);
});

function invKey(entry: InventoryEntry) {
  return `${entry.itemId}-${entry.enchantment?.id || "base"}-${entry.instanceIds?.[0] || "stack"}`;
}

function selectStock(id: string) {
  selectedStockId.value = id;
  qty.value = 1;
}

async function selectInventory(entry: InventoryEntry) {
  selectedInvKey.value = invKey(entry);
  selectedInv.value = entry;
  qty.value = 1;
  quote.value = null;
  if (!props.npcId || !session.token || !session.character) return;
  const res = await apiShopQuote(session.token, session.character.id, props.npcId, entry.itemId);
  if (!res.ok || !res.data) {
    addToast("error" in res ? res.error?.message || "Quote failed" : "Quote failed", "warning");
    return;
  }
  quote.value = res.data as ShopQuoteResponse;
}

async function loadShop(options?: { resetSelection?: boolean; showLoading?: boolean }) {
  if (!props.npcId) return;
  if (!session.token || !session.character) return;
  const resetSelection = options?.resetSelection ?? true;
  const showLoading = options?.showLoading ?? true;
  if (showLoading) {
    loading.value = true;
  } else {
    refreshing.value = true;
  }
  error.value = null;
  try {
    const snapshot = await sessionSocket.requestSnapshot("shop", { npcId: props.npcId });
    shop.value = snapshot.data as ShopBrowseResponse;
    lastUpdatedAt.value = Date.now();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    error.value = message || "Failed to load shop. Please retry.";
    return;
  } finally {
    if (showLoading) {
      loading.value = false;
    } else {
      refreshing.value = false;
    }
  }
  if (resetSelection) {
    selectedStockId.value = null;
    selectedInvKey.value = null;
    selectedInv.value = null;
    quote.value = null;
    return;
  }

  const stockList = shop.value?.stock ?? [];
  if (selectedStockId.value && !stockList.some((entry) => entry.id === selectedStockId.value)) {
    selectedStockId.value = null;
  }

  if (selectedInvKey.value) {
    const inv = (session.character?.inventory ?? []).find((entry) => invKey(entry) === selectedInvKey.value) ?? null;
    if (inv) {
      selectedInv.value = inv;
    } else {
      selectedInvKey.value = null;
      selectedInv.value = null;
      quote.value = null;
    }
  } else {
    selectedInv.value = null;
    quote.value = null;
  }
}

async function buySelected() {
  if (!props.npcId || !selectedStock.value) return;
  if (!session.token || !session.character) return;
  loading.value = true;
  const res = await apiShopBuy(session.token, session.character.id, props.npcId, {
    itemId: selectedStock.value.itemId,
    quantity: Math.max(1, Math.floor(qty.value))
  });
  loading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Buy failed" : "Buy failed", "warning");
    return;
  }
  addToast("Bought item", "success");
  await session.refreshCharacterSnapshot();
  await loadShop({ resetSelection: false, showLoading: false });
}

async function sellSelected() {
  if (!props.npcId || !selectedInv.value) return;
  if (!session.token || !session.character) return;
  loading.value = true;
  const res = await apiShopSell(session.token, session.character.id, props.npcId, {
    itemId: selectedInv.value.itemId,
    quantity: Math.max(1, Math.floor(qty.value))
  });
  loading.value = false;
  if (!res.ok) {
    addToast("error" in res ? res.error?.message || "Sell failed" : "Sell failed", "warning");
    return;
  }
  addToast("Sold item", "success");
  await session.refreshCharacterSnapshot();
  await loadShop({ resetSelection: false, showLoading: false });
}

watch(
  () => props.npcId,
  (next) => {
    if (!next) {
      stopShopStream();
      shop.value = null;
      error.value = null;
      selectedStockId.value = null;
      selectedInvKey.value = null;
      selectedInv.value = null;
      quote.value = null;
      return;
    }
    startShopStream();
    void loadShop({ resetSelection: true });
  },
  { immediate: true }
);

watch(
  () => mode.value,
  () => {
    query.value = "";
    qty.value = 1;
  }
);

onMounted(() => {
  handleVisibilityChange();
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onUnmounted(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  stopShopStream();
});

function handleVisibilityChange() {
  if (!document.hidden && props.npcId) {
    void loadShop({ resetSelection: false, showLoading: false });
  }
}
</script>

<style scoped>
.shop-session {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.controls {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.search {
  display: inline-flex;
}

.ui-input {
  width: 170px;
  padding: 0.45rem 0.6rem;
}

.price-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.kv-label {
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.kv-value {
  font-size: 1.1rem;
  font-weight: 800;
  margin-top: 0.25rem;
}

.list {
  margin-top: 0.75rem;
  display: grid;
  gap: 0.6rem;
}

.row {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 0.65rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  text-align: left;
}

.row.selected {
  border-color: color-mix(in srgb, var(--accent) 40%, var(--glass-border-0));
  box-shadow: var(--shadow-accent);
}

.icon {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
}

.row-main {
  min-width: 0;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.6rem;
}

.name {
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trade-box {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: var(--radius-2);
  border: 1px dashed var(--glass-border-0);
  background: var(--glass-0);
  display: grid;
  gap: 0.75rem;
}

.trade-box--inline {
  margin-top: 0;
}

.trade-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.trade-controls {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.field {
  display: grid;
  gap: 0.3rem;
}

.field-label {
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--fg-1);
}
</style>
